import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import {PlaceOutlined} from "@mui/icons-material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {t} from "i18next";
import {httpClient} from "../utils/httpClient";
import AppConsts from "../app/AppConsts";
import {logError} from "../utils/logging";
import {useSSOPublicClient} from "../user-auth/OAuth";
import {useSnackbar} from "../providers/SnackbarProvider";

const LAST_24_HOURS_MS = 24 * 60 * 60 * 1000;

function buildGoogleMapsUrl(gpsLocation) {
    if (!gpsLocation || gpsLocation.latitude == null || gpsLocation.longitude == null) {
        return null;
    }
    return `https://www.google.com/maps/search/?api=1&query=${gpsLocation.latitude},${gpsLocation.longitude}`;
}

function EmergencyCallCard({call, importingId, onStart}) {
    const mapsUrl = buildGoogleMapsUrl(call.gpsLocation);

    return (
        <Card variant="outlined">
            <CardContent sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: '150px 1fr auto'},
                alignItems: 'center',
                gap: 2
            }}>
                <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                        Telefono
                    </Typography>
                    <Typography variant="body1">
                        {call.contact || '-'}
                    </Typography>
                </Stack>

                {mapsUrl
                    ? <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        startIcon={<PlaceOutlined fontSize="small"/>}
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{justifySelf: 'start'}}>
                        Mostra posizione
                    </Button>
                    : <Box/>}

                <Button
                    variant="outlined"
                    disabled={importingId !== null}
                    startIcon={importingId === call.id
                        ? <CircularProgress size={16} color="inherit"/>
                        : null}
                    onClick={() => onStart(call.id)}
                    sx={{justifySelf: {xs: 'stretch', sm: 'end'}}}>
                    Avvia triage
                </Button>
            </CardContent>
        </Card>
    );
}

export function ImportPreHospitalWorkflowDialog({onClose, onImported, onRequireSSOLogin}) {
    const sso = useSSOPublicClient();
    const {createSnackbar} = useSnackbar();
    const [fiscalCode, setFiscalCode] = useState("");
    const [phase, setPhase] = useState("idle");
    const [emergencyCalls, setEmergencyCalls] = useState([]);
    const [importingId, setImportingId] = useState(null);
    const [alreadyImportedError, setAlreadyImportedError] = useState(null);

    useEffect(() => {
        sso.runSilentCheck().then(() => {
            setPhase("ready");
        }).catch(e => {
            logError(e);
            setPhase("sso-required");
        });
    }, [sso]);

    const {recentCalls, olderCalls} = useMemo(() => {
        const cutoff = Date.now() - LAST_24_HOURS_MS;
        const recent = [];
        const older = [];

        emergencyCalls.forEach(call => {
            const callTime = call.date ? new Date(call.date).getTime() : null;
            if (callTime !== null && callTime >= cutoff) {
                recent.push(call);
            } else {
                older.push(call);
            }
        });

        return {recentCalls: recent, olderCalls: older};
    }, [emergencyCalls]);

    const handleSearch = useCallback(() => {
        if (!fiscalCode.trim()) {
            return;
        }

        setPhase("searching");
        setAlreadyImportedError(null);

        httpClient.get(`${AppConsts.API_PATH}/besidetech/emergency-calls`, {
            params: {fiscalCode: fiscalCode.trim()},
            headers: {"X-Besidetech-Token": sso.getToken()}
        }).then(response => {
            if (response.data.errors) {
                setPhase("error");
                return;
            }
            setEmergencyCalls(response.data);
            setPhase(response.data.length === 0 ? "empty" : "results");
        }).catch(e => {
            logError(e);
            setPhase("error");
        });
    }, [fiscalCode, sso]);

    const handleStart = useCallback((emergencyCallId) => {
        setImportingId(emergencyCallId);
        setAlreadyImportedError(null);

        httpClient.post(
            `${AppConsts.API_PATH}/besidetech/emergency-calls/${emergencyCallId}/pre-hospital-workflow`,
            {},
            {headers: {"X-Besidetech-Token": sso.getToken()}}
        ).then(response => {
            if (response.data.id) {
                onImported(response.data.id);
                onClose();
            } else {
                const error = response.data.errors[0];
                if (error.code === "preHospitalWorkflow.alreadyImported" && error.details?.workflowId) {
                    setAlreadyImportedError({message: error.message, workflowId: error.details.workflowId});
                } else {
                    createSnackbar({message: error.message, autoHideDuration: 3000});
                }
                setImportingId(null);
            }
        }).catch(e => {
            logError(e);
            createSnackbar({message: t('common.sorry_something_went_wrong'), autoHideDuration: 3000});
            setImportingId(null);
        });
    }, [sso, onImported, onClose, createSnackbar]);

    return (
        <>
            <DialogTitle>Importa triage</DialogTitle>
            <DialogContent>
                {phase === "idle" &&
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4}}>
                        <CircularProgress size={32}/>
                        <Typography color="text.secondary">
                            Sincronizzazione con la piattaforma Stroke in corso...
                        </Typography>
                    </Box>}

                {phase !== "idle" &&
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci il codice fiscale del paziente per cercare le chiamate di emergenza registrate
                            nella piattaforma Stroke.
                        </Typography>

                        <TextField
                            label="Codice fiscale"
                            value={fiscalCode}
                            onChange={e => setFiscalCode(e.target.value)}
                            fullWidth
                            disabled={phase === "searching"}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    handleSearch();
                                }
                            }}
                        />

                        {phase === "sso-required" &&
                            <Alert severity="warning" action={
                                <Button color="inherit" size="small" onClick={onRequireSSOLogin}>
                                    {t('sign_in.sign_in_with_sso')}
                                </Button>
                            }>
                                Accedi tramite SSO per effettuare la ricerca delle chiamate
                            </Alert>}

                        {phase === "error" &&
                            <Alert severity="error">
                                Si è verificato un errore durante la ricerca, riprova.
                            </Alert>}

                        {phase === "empty" &&
                            <Alert severity="info">
                                Nessuna chiamata trovata, verifica la correttezza del codice fiscale.
                            </Alert>}

                        {phase === "searching" &&
                            <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                                <CircularProgress size={28}/>
                            </Box>}

                        {phase === "results" &&
                            <Stack spacing={3}>
                                {recentCalls.length > 0 &&
                                    <Stack spacing={2}>
                                        <Typography variant="subtitle2">
                                            Chiamate delle ultime 24 ore
                                        </Typography>
                                        {recentCalls.map(call => (
                                            <EmergencyCallCard
                                                key={call.id}
                                                call={call}
                                                importingId={importingId}
                                                onStart={handleStart}
                                            />
                                        ))}
                                    </Stack>}

                                {olderCalls.length > 0 &&
                                    <Stack spacing={2}>
                                        <Alert severity="warning">
                                            Le chiamate seguenti risalgono a più di 24 ore fa: verifica che siano
                                            ancora pertinenti prima di importarle.
                                        </Alert>
                                        {olderCalls.map(call => (
                                            <EmergencyCallCard
                                                key={call.id}
                                                call={call}
                                                importingId={importingId}
                                                onStart={handleStart}
                                            />
                                        ))}
                                    </Stack>}
                            </Stack>}
                    </Stack>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Chiudi</Button>
                {phase !== "idle" &&
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={phase === "searching" || !fiscalCode.trim()}>
                        Cerca
                    </Button>}
            </DialogActions>

            <Dialog open={Boolean(alreadyImportedError)} onClose={() => setAlreadyImportedError(null)}>
                <DialogTitle>Triage già esistente</DialogTitle>
                <DialogContent>
                    <Typography>
                        {alreadyImportedError?.message}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlreadyImportedError(null)}>Annulla</Button>
                    <Button
                        variant="contained"
                        onClick={() => onImported(alreadyImportedError.workflowId)}>
                        Vai al triage esistente
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
